import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import PostAddOutlinedIcon from "@mui/icons-material/PostAddOutlined";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import Page from "../Components/Page";
import { axiosInstance } from "../Components/axiosInstance.js";

function unwrapList(payload) {
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return Array.isArray(payload) ? payload : [];
}

const DASHBOARD_COPY = {
  admin: {
    title: "Admin dashboard",
    intro: "Review access requests, workforce accounts, and support workflows across the platform.",
    sectionTitle: "Administration overview",
  },
  partner: {
    title: "Partner dashboard",
    intro: "Monitor project activity and move directly into publishing, enrollment, and consented-data work.",
    sectionTitle: "Project portfolio",
  },
};

const ACTIONS = {
  admin: [
    { label: "Review project approvals", description: "Decide pending grower enrollment requests.", to: "/project-enrollments", icon: AssignmentTurnedInOutlinedIcon },
    { label: "Manage workforce", description: "Provision Partner and Aggregator users and review access health.", to: "/workforce", icon: GroupOutlinedIcon },
    { label: "Support a user", description: "Find a platform user and enter an audited support session.", to: "/persona", icon: PersonSearchOutlinedIcon },
  ],
  partner: [
    { label: "Manage projects", description: "Review drafts, active projects, enrollment, and data access.", to: "/projects", icon: WorkspacesOutlinedIcon },
    { label: "Create a project", description: "Define criteria, timeline, requirements, and consented data scope.", to: "/projects/new", icon: PostAddOutlinedIcon },
    { label: "Review profile", description: "Confirm your workspace identity and assigned access.", to: "/profile", icon: GroupOutlinedIcon },
  ],
};

function Metric({ label, value, detail }) {
  return (
    <div className="rounded-[8px] border border-slate-200 bg-white px-5 py-5 shadow-[0_8px_22px_rgba(18,28,45,0.04)]">
      <p className="text-xs font-bold uppercase text-slate-500">{label}</p>
      <p className="mt-2 text-3xl font-black text-slate-950">{value}</p>
      <p className="mt-1 text-sm text-slate-500">{detail}</p>
    </div>
  );
}

export default function PortalDashboardPage({ portal }) {
  const copy = DASHBOARD_COPY[portal] || DASHBOARD_COPY.admin;
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function loadDashboard() {
      setError("");
      setMetrics(null);
      try {
        if (portal === "partner") {
          const response = await axiosInstance.get("/partner/projects");
          const projects = unwrapList(response.data);
          if (!active) return;
          setMetrics([
            { label: "Projects", value: projects.length, detail: "Across draft, active, and closed states" },
            { label: "Active", value: projects.filter((item) => item.status === "ACTIVE").length, detail: "Currently published to eligible growers" },
            { label: "Enrolled growers", value: projects.reduce((sum, item) => sum + Number(item.counts?.opted_in || 0), 0), detail: "Administrator-approved participation" },
          ]);
          return;
        }

        const [approvalResponse, workforceResponse] = await Promise.all([
          axiosInstance.get("/admin/project-enrollments", { params: { consent_status: "PENDING_APPROVAL" } }),
          axiosInstance.get("/admin/platform-users", { params: { workforce_only: true, page: 1, page_size: 1 } }),
        ]);
        if (!active) return;
        const approvals = unwrapList(approvalResponse.data);
        const workforcePayload = workforceResponse.data?.data || workforceResponse.data || {};
        setMetrics([
          { label: "Pending approvals", value: approvals.length, detail: "Grower project requests awaiting review" },
          { label: "Workforce users", value: Number(workforcePayload.total ?? unwrapList(workforcePayload).length), detail: "Partner and Aggregator accounts" },
          { label: "Support tools", value: 3, detail: "Approvals, provisioning, and impersonation" },
        ]);
      } catch (loadError) {
        if (active) {
          setError("Dashboard information could not be loaded. You can still open each workspace below.");
          setMetrics([]);
        }
      }
    }

    loadDashboard();
    return () => { active = false; };
  }, [portal]);

  const actions = useMemo(() => ACTIONS[portal] || ACTIONS.admin, [portal]);

  return (
    <Page
      title={copy.title}
      headerVariant="hybrid"
      contentWidth="standard"
      padding="px-4 py-6 md:px-6 md:py-8"
      introContent={<p>{copy.intro}</p>}
    >
      <div className="w-full space-y-8">
        {error ? <Alert severity="warning">{error}</Alert> : null}

        <section>
          <div className="mb-4">
            <h2 className="text-xl font-black text-slate-950">{copy.sectionTitle}</h2>
            <p className="mt-1 text-sm text-slate-600">A concise view of the work currently attached to this portal.</p>
          </div>
          {metrics === null ? (
            <div className="flex min-h-[150px] items-center justify-center"><CircularProgress color="success" /></div>
          ) : metrics.length ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {metrics.map((item) => <Metric {...item} key={item.label} />)}
            </div>
          ) : null}
        </section>

        <section className="border-t border-slate-200 pt-6">
          <h2 className="text-xl font-black text-slate-950">Continue your work</h2>
          <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-3">
            {actions.map((action) => {
              const Icon = action.icon;
              return (
                <Link className="group rounded-[8px] border border-slate-200 bg-white p-5 transition hover:border-[#237a43] hover:shadow-[0_8px_20px_rgba(18,28,45,0.05)]" key={action.to} to={action.to}>
                  <div className="flex items-start justify-between gap-4">
                    <Icon sx={{ color: "#237a43", fontSize: 24 }} />
                    <ArrowForwardOutlinedIcon className="text-slate-400 transition group-hover:text-[#237a43]" sx={{ fontSize: 19 }} />
                  </div>
                  <h3 className="mt-4 font-black text-slate-950">{action.label}</h3>
                  <p className="mt-1 text-sm leading-5 text-slate-600">{action.description}</p>
                </Link>
              );
            })}
          </div>
        </section>
      </div>
    </Page>
  );
}
